import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit, Search, ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { fetchDeliveryAddresses, fetchDeliveryAddressesCount } from '../services/api.ts';
import './DeliveryAddresses.css';

interface DeliveryAddress {
  id: number;
  user_id: string;
  address_line_1: string;
  address_line_2: string;
  town: string;
  state_id: number;
  state_name: string;
  postcode: string;
  country_id: number;
  country_name: string;
  is_default: boolean;
  enable: boolean;
  created_at: string;
  user_name: string | null;
}

const DeliveryAddresses: React.FC = () => {
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState<DeliveryAddress[]>([]);
  const [totalAddresses, setTotalAddresses] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const addressesPerPage = 12;

  useEffect(() => {
    const loadAddresses = async () => {
      try {
        setLoading(true);
        const offset = (currentPage - 1) * addressesPerPage;
        const [addressesData, countData] = await Promise.all([
          fetchDeliveryAddresses(offset, addressesPerPage),
          fetchDeliveryAddressesCount()
        ]);
        setAddresses(addressesData);
        setTotalAddresses(countData);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load delivery addresses');
        console.error('Error loading delivery addresses:', err);
      } finally {
        setLoading(false);
      }
    };

    loadAddresses();
  }, [currentPage]);

  const filteredAddresses = addresses.filter((address) => {
    const searchStr = `${address.address_line_1} ${address.address_line_2} ${address.town} ${address.postcode}`.toLowerCase();
    return searchStr.includes(searchTerm.toLowerCase());
  });

  const totalPages = Math.ceil(totalAddresses / addressesPerPage);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <div className="delivery-addresses-page">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading delivery addresses...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="delivery-addresses-page">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="delivery-addresses-page">
      <div className="page-header">
        <h2>Delivery Addresses</h2>
        <div className="page-actions">
          <div className="search-box">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search addresses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button className="add-btn" onClick={() => navigate('/dashboard/delivery-addresses/new')}>
            <Plus size={18} />
            Add New Address
          </button>
        </div>
      </div>

      <div className="filter-section">
        <div className="filter-group">
          <label>Showing:</label>
          <span style={{ color: '#6366f1', fontWeight: 600 }}>
            {filteredAddresses.length} of {totalAddresses} addresses
          </span>
        </div>
      </div>

      {filteredAddresses.length === 0 ? (
        <div className="empty-state">
          <p>No delivery addresses found matching your criteria.</p>
        </div>
      ) : (
        <>
          <div className="da-table-wrapper">
            <table className="da-table">
              <thead>
                <tr>
                  <th>Address</th>
                  <th>Town</th>
                  <th>Postcode</th>
                  <th>State</th>
                  <th>Country</th>
                  <th>User</th>
                  <th>Default</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAddresses.map((address) => (
                  <tr key={address.id}>
                    <td>
                      <div className="da-address-cell">
                        <span className="da-line1">{address.address_line_1}</span>
                        {address.address_line_2 && (
                          <span className="da-line2">{address.address_line_2}</span>
                        )}
                      </div>
                    </td>
                    <td>{address.town}</td>
                    <td><span className="da-postcode">{address.postcode}</span></td>
                    <td>{address.state_name || `ID: ${address.state_id}`}</td>
                    <td>{address.country_name || `ID: ${address.country_id}`}</td>
                    <td>
                      {address.user_name
                        ? <span title={address.user_id}>{address.user_name}</span>
                        : <span className="da-user-id" title={address.user_id}>{address.user_id.substring(0, 8)}...</span>}
                    </td>
                    <td>
                      {address.is_default
                        ? <span className="da-badge da-badge-default">Default</span>
                        : <span className="da-badge da-badge-normal">—</span>}
                    </td>
                    <td>
                      <span className={`da-badge ${address.enable ? 'da-badge-active' : 'da-badge-inactive'}`}>
                        {address.enable ? 'Active' : 'Disabled'}
                      </span>
                    </td>
                    <td>
                      <button
                        className="da-edit-btn"
                        onClick={() => navigate(`/dashboard/delivery-addresses/edit/${address.id}`)}
                        title="Edit"
                      >
                        <Edit size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={18} />
                Previous
              </button>

              <div className="pagination-info">
                Page {currentPage} of {totalPages}
              </div>

              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
                <ChevronRight size={18} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DeliveryAddresses;
